import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, Trash, Plus, CircleNotch } from '@phosphor-icons/react';

const SEV = {
  alta: { dot: '🔴', badge: 'bg-red-500/15 text-red-600 border-red-500/40', border: 'border-red-500/40' },
  media: { dot: '🟠', badge: 'bg-amber-500/15 text-amber-600 border-amber-500/40', border: 'border-amber-500/40' },
  ok: { dot: '🟢', badge: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/40', border: 'border-[var(--border)]' },
};

const EMPTY = { titulo: '', detalle: '', responsable: '', accion: '', severidad: 'media' };

export const AlertasGerenciales = ({ users = [] }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/work-plan/alertas-gerenciales').then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const crear = async () => {
    if (!f.titulo.trim()) return toast.error('Escribe el título de la alerta');
    setSaving(true);
    try {
      await api.post('/work-plan/alertas-gerenciales', f);
      toast.success('Alerta agregada');
      setOpen(false);
      setF(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const resolver = async (id) => {
    try {
      await api.patch(`/work-plan/alertas-gerenciales/${id}/resolver`);
      toast.success('Alerta marcada como resuelta');
      load();
    } catch { toast.error('Error al resolver'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta alerta manual?')) return;
    try {
      await api.delete(`/work-plan/alertas-gerenciales/${id}`);
      toast.success('Alerta eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <Card className="p-4 bg-[var(--surface)] border-[var(--border)]" data-testid="alertas-gerenciales-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)]">🚨 Alertas Gerenciales</h3>
          <p className="text-[10px] text-[var(--text-muted)]">5 alertas automáticas del CRM + alertas manuales con responsable y acción sugerida</p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#6C3FB5] hover:bg-[#4B1D73] text-white font-bold"
          data-testid="btn-agregar-alerta">
          <Plus size={14} weight="bold" className="mr-1" /> Agregar alerta
        </Button>
      </div>

      {!data ? (
        <p className="text-center py-6 text-xs text-[var(--text-muted)]"><CircleNotch size={14} className="animate-spin inline mr-1" /> Analizando el CRM…</p>
      ) : (
        <div className="space-y-2">
          {data.alertas.map((a) => {
            const s = SEV[a.severidad] || SEV.media;
            return (
              <div key={a.key} className={`border rounded-[12px] p-3 ${s.border} ${a.severidad === 'ok' ? 'opacity-70' : ''}`}
                data-testid={`alerta-auto-${a.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-black text-[var(--text)]">{s.dot} {a.titulo}</p>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border shrink-0 ${s.badge}`}>
                    {a.severidad === 'ok' ? 'OK' : `${a.count} · ${a.severidad.toUpperCase()}`}
                  </span>
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-1">{a.detalle}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-[10px]">
                  <span><b className="text-[#6C3FB5] uppercase">Responsable:</b> <span className="text-[var(--text)] font-bold">{a.responsable}</span></span>
                  <span><b className="text-[#6C3FB5] uppercase">Acción sugerida:</b> <span className="text-[var(--text-muted)]">{a.accion}</span></span>
                </div>
              </div>
            );
          })}

          {data.manuales.length > 0 && (
            <>
              <p className="text-[10px] uppercase font-black text-[var(--text-faint)] pt-2">📌 Alertas manuales</p>
              {data.manuales.map((m) => {
                const s = SEV[m.severidad] || SEV.media;
                return (
                  <div key={m.id} className={`border rounded-[12px] p-3 ${s.border}`} data-testid={`alerta-manual-${m.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-black text-[var(--text)]">{s.dot} {m.titulo}</p>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => resolver(m.id)} title="Marcar resuelta"
                          className="p-1.5 rounded-md bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 hover:bg-emerald-500/25"
                          data-testid={`btn-resolver-${m.id}`}>
                          <CheckCircle size={14} weight="bold" />
                        </button>
                        <button onClick={() => eliminar(m.id)} title="Eliminar"
                          className="p-1.5 rounded-md bg-red-500/10 text-red-500 border border-red-500/30 hover:bg-red-500/20"
                          data-testid={`btn-eliminar-${m.id}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                    {m.detalle && <p className="text-xs text-[var(--text-muted)] mt-1">{m.detalle}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-[10px]">
                      {m.responsable && <span><b className="text-[#6C3FB5] uppercase">Responsable:</b> <span className="text-[var(--text)] font-bold">{m.responsable}</span></span>}
                      {m.accion && <span><b className="text-[#6C3FB5] uppercase">Acción sugerida:</b> <span className="text-[var(--text-muted)]">{m.accion}</span></span>}
                      <span className="text-[var(--text-faint)]">Creada por {m.creada_por}</span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[var(--surface)] border-[var(--border)]" data-testid="modal-alerta-manual">
          <DialogHeader>
            <DialogTitle>📌 Nueva alerta manual</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título *</Label>
              <Input value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })}
                placeholder="Ej: Dar seguimiento a proyecto Hotel Antigua" className="bg-[var(--bg)] border-[var(--border)]"
                data-testid="alerta-titulo-input" />
            </div>
            <div>
              <Label>Detalle</Label>
              <Textarea rows={2} value={f.detalle} onChange={(e) => setF({ ...f, detalle: e.target.value })}
                placeholder="Contexto de la alerta…" className="bg-[var(--bg)] border-[var(--border)]"
                data-testid="alerta-detalle-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Responsable</Label>
                <select value={f.responsable} onChange={(e) => setF({ ...f, responsable: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
                  data-testid="alerta-responsable-select">
                  <option value="">— Elegir —</option>
                  {users.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Severidad</Label>
                <select value={f.severidad} onChange={(e) => setF({ ...f, severidad: e.target.value })}
                  className="w-full px-3 py-2 text-sm rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]"
                  data-testid="alerta-severidad-select">
                  <option value="media">🟠 Media</option>
                  <option value="alta">🔴 Alta</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Acción sugerida</Label>
              <Input value={f.accion} onChange={(e) => setF({ ...f, accion: e.target.value })}
                placeholder="Ej: Llamar al cliente antes del viernes" className="bg-[var(--bg)] border-[var(--border)]"
                data-testid="alerta-accion-input" />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="alerta-cancelar">Cancelar</Button>
              <Button onClick={crear} disabled={saving} className="bg-[#6C3FB5] hover:bg-[#4B1D73] text-white font-bold"
                data-testid="alerta-guardar">
                {saving ? <CircleNotch size={16} className="animate-spin" /> : 'Guardar alerta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
